import React, { Suspense, lazy, Component } from 'react';

// Detecta erros de carregamento de chunk (ChunkLoadError após novo deploy)
const isChunkLoadError = (error) =>
  error?.name === 'ChunkLoadError' ||
  /Loading chunk \d+ failed/i.test(error?.message || '') ||
  /Failed to fetch dynamically imported module/i.test(error?.message || '') ||
  /Importing a module script failed/i.test(error?.message || '');

// Soft reload: recarrega forçando index.html fresco, mas PRESERVA o cache do
// SW (fallback offline). É a 1ª linha de recuperação — a esmagadora maioria dos
// ChunkLoadError em celular é blip de rede 5G/4G, e o arquivo existe no servidor.
// Apagar o cache nesse caso é contraproducente (destrói o offline sem resolver).
function softReload() {
  try {
    const u = new URL(window.location.href);
    u.searchParams.set('_cb', Date.now());
    window.location.replace(u.toString());
  } catch (_) {
    window.location.reload();
  }
}

// Nuclear: apaga todo o cache + desregistra o SW. Reservado para o botão manual,
// quando o soft reload já não resolveu (raro — deploy inconsistente de verdade).
async function clearCachesAndReload() {
  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch (_) { /* seguro ignorar */ }
  softReload();
}

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, isReloading: false, clearing: false };
    this.handleClear = this.handleClear.bind(this);
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error, isReloading: isChunkLoadError(error) };
  }
  componentDidCatch(error, info) {
    console.error('ErrorBoundary caught:', error, info);
  }
  componentDidUpdate(_prev, prevState) {
    if (this.state.isReloading && !prevState.isReloading) {
      const RELOAD_KEY = 'eb_chunk_reload_ts';
      const last = parseInt(sessionStorage.getItem(RELOAD_KEY) || '0', 10);
      if (Date.now() - last > 30_000) {
        sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
        // 1ª tentativa: soft reload (preserva cache/SW). Resolve o caso comum —
        // blip de rede móvel — sem destruir o offline. Se falhar de novo dentro
        // de 30s, cai na tela manual com o botão "nuclear" (clearCachesAndReload).
        softReload();
      } else {
        this.setState({ isReloading: false });
      }
    }
  }
  handleClear() {
    this.setState({ clearing: true });
    clearCachesAndReload();
  }
  render() {
    if (this.state.isReloading) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#fff', fontFamily: 'monospace' }}>
          <p>Atualizando app...</p>
        </div>
      );
    }
    if (this.state.hasError) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#fff', fontFamily: 'monospace', maxWidth: 360, margin: '0 auto' }}>
          <p style={{ marginBottom: '1.5rem' }}>Não foi possível carregar o app. Verifique sua conexão e toque abaixo para limpar o cache e recarregar.</p>
          <button
            onClick={this.handleClear}
            disabled={this.state.clearing}
            style={{
              background: '#e94560', color: '#fff', border: 'none', borderRadius: 8,
              padding: '0.75rem 1.5rem', fontSize: 15, fontFamily: 'inherit',
              cursor: this.state.clearing ? 'default' : 'pointer', opacity: this.state.clearing ? 0.7 : 1,
              width: '100%', marginBottom: '0.75rem'
            }}
          >
            {this.state.clearing ? 'Limpando...' : 'Limpar cache e recarregar'}
          </button>
          {this.state.error && (
            <p style={{ fontSize: 11, marginTop: '1rem', color: '#888', wordBreak: 'break-word' }}>
              {(this.state.error.name ? this.state.error.name + ': ' : '') + (this.state.error.message || String(this.state.error)).slice(0, 200)}
            </p>
          )}
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <pre style={{ textAlign: 'left', fontSize: 11, marginTop: '1rem', background: '#111', padding: '1rem', borderRadius: 6, overflow: 'auto', maxHeight: 300 }}>
              {this.state.error.stack}
            </pre>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from 'sonner';
import './App.css';

// Lazy import com retry: ChunkLoadError em celular é quase sempre falha
// transiente de rede 5G/4G ao baixar o chunk (o arquivo existe no servidor).
// Tenta de novo até 5x com backoff exponencial (~0.4→3s, ~9s no total) antes de
// propagar o erro pro ErrorBoundary. Se o aparelho estiver offline, espera a
// conexão voltar (até 8s) em vez de gastar uma tentativa à toa. Recupera no
// próprio lugar, sem reload, na maioria dos blips — evita a tela "Erro no app".
function lazyWithRetry(importer) {
  return lazy(async () => {
    let lastErr;
    for (let attempt = 0; attempt < 5; attempt++) {
      try {
        return await importer();
      } catch (err) {
        lastErr = err;
        if (!isChunkLoadError(err)) throw err;
        // Offline: espera o evento 'online' (ou 8s) antes da próxima tentativa.
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
          await new Promise((resolve) => {
            const t = setTimeout(resolve, 8000);
            window.addEventListener('online', () => { clearTimeout(t); resolve(); }, { once: true });
          });
        }
        // Backoff exponencial limitado: 0.4s, 0.8s, 1.6s, 3s, 3s.
        await new Promise((r) => setTimeout(r, Math.min(400 * 2 ** attempt, 3000)));
      }
    }
    throw lastErr;
  });
}

// Lazy load all pages for better performance
const Login = lazyWithRetry(() => import('./pages/Login'));
const Register = lazyWithRetry(() => import('./pages/Register'));
const ForgotPassword = lazyWithRetry(() => import('./pages/ForgotPassword'));
const ResetPassword = lazyWithRetry(() => import('./pages/ResetPassword'));
const Dashboard = lazyWithRetry(() => import('./pages/Dashboard'));
const InstallerDashboard = lazyWithRetry(() => import('./pages/InstallerDashboard'));
const InstallerJobDetail = lazyWithRetry(() => import('./pages/InstallerJobDetail'));
const InstallerCalendar = lazyWithRetry(() => import('./pages/InstallerCalendar'));
const Jobs = lazyWithRetry(() => import('./pages/Jobs'));
const JobDetail = lazyWithRetry(() => import('./pages/JobDetail'));
const Users = lazyWithRetry(() => import('./pages/Users'));
const Calendar = lazyWithRetry(() => import('./pages/Calendar'));
const CheckinViewer = lazyWithRetry(() => import('./pages/CheckinViewer'));
const Checkins = lazyWithRetry(() => import('./pages/Checkins'));
const UnifiedReports = lazyWithRetry(() => import('./pages/UnifiedReports'));
const FamilyReport = lazyWithRetry(() => import('./pages/FamilyReport'));
const InstallerReport = lazyWithRetry(() => import('./pages/InstallerReport'));
const FamilyKPIsReport = lazyWithRetry(() => import('./pages/FamilyKPIsReport'));
const Profile = lazyWithRetry(() => import('./pages/Profile'));
const SchedulerAdmin = lazyWithRetry(() => import('./pages/SchedulerAdmin'));
const VisitasTecnicas = lazyWithRetry(() => import('./pages/VisitasTecnicas'));
const VisitaDetail = lazyWithRetry(() => import('./pages/VisitaDetail'));
const VisitasRelatorios = lazyWithRetry(() => import('./pages/VisitasRelatorios'));

// Non-lazy imports for layout components (always needed)
import Sidebar from './components/layout/Sidebar';
import BottomNav from './components/layout/BottomNav';
import UpdateNotification from './components/UpdateNotification';

// Loading spinner component
const PageLoader = () => (
  <div className="flex items-center justify-center h-[40vh]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

// Banner de aviso de conexão offline
// Debounce de 4s antes de mostrar — evita falso positivo em mobile quando
// o dispositivo troca de rede (WiFi → dados) e navigator.onLine fica false
// por alguns instantes antes do evento "online" disparar.
const OfflineBanner = () => {
  const [offline, setOffline] = React.useState(false);
  const timerRef = React.useRef(null);

  React.useEffect(() => {
    const showBanner = () => {
      timerRef.current = setTimeout(() => setOffline(true), 4000);
    };
    const hideBanner = () => {
      clearTimeout(timerRef.current);
      setOffline(false);
    };

    // Só agenda o banner se já começou offline
    if (!navigator.onLine) showBanner();

    window.addEventListener('online', hideBanner);
    window.addEventListener('offline', showBanner);
    return () => {
      clearTimeout(timerRef.current);
      window.removeEventListener('online', hideBanner);
      window.removeEventListener('offline', showBanner);
    };
  }, []);

  if (!offline) return null;
  return (
    <div className="fixed top-0 left-0 right-0 z-[100] flex items-center justify-center gap-2 bg-yellow-500/95 text-black text-xs font-semibold py-2 px-4 text-center" role="alert">
      <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/></svg>
      Sem conexão — modo offline
    </div>
  );
};

// App-shell skeleton: renderiza estrutura (sidebar + conteúdo) antes do auth resolver
const AppShellSkeleton = () => (
  <div className="min-h-screen bg-background">
    {/* Sidebar placeholder (desktop) */}
    <div className="hidden md:fixed md:inset-y-0 md:flex md:w-64 md:flex-col bg-card border-r border-white/5" />
    <div className="md:pl-64 flex flex-col flex-1">
      <main className="flex-1 pb-20 md:pb-0">
        <div className="p-4 md:p-8 space-y-6 animate-pulse">
          {/* Header skeleton */}
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <div className="h-7 w-40 rounded bg-white/10" />
              <div className="h-3 w-24 rounded bg-white/7" />
            </div>
            <div className="h-10 w-36 rounded-lg bg-white/10" />
          </div>
          {/* Stats skeleton */}
          <div className="grid grid-cols-3 gap-3 md:gap-6">
            {[0,1,2].map(i => (
              <div key={i} className="rounded-xl bg-card border border-white/5 p-4 space-y-2">
                <div className="h-3 w-16 rounded bg-white/10" />
                <div className="h-7 w-8 rounded bg-white/15" />
              </div>
            ))}
          </div>
          {/* Card skeletons */}
          <div className="space-y-3">
            <div className="h-5 w-36 rounded bg-white/10" />
            {[0,1].map(i => (
              <div key={i} className="rounded-xl bg-card border border-white/5 p-4 space-y-3">
                <div className="h-4 w-3/4 rounded bg-white/10" />
                <div className="h-3 w-1/2 rounded bg-white/7" />
                <div className="h-11 w-full rounded-lg bg-primary/20" />
              </div>
            ))}
          </div>
        </div>
      </main>
      {/* Bottom nav placeholder (mobile) */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 h-16 bg-card border-t border-white/5" />
    </div>
  </div>
);

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <AppShellSkeleton />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const MainLayout = ({ children }) => {
  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      <div className="md:pl-64 flex flex-col flex-1">
        <main className="flex-1 pb-20 md:pb-0">
          <Suspense fallback={<PageLoader />}>
            {children}
          </Suspense>
        </main>
        <BottomNav />
      </div>
    </div>
  );
};

// Layout sem sidebar — para instaladores (100% mobile, sem desperdiçar espaço)
const InstallerLayout = ({ children }) => {
  return (
    <div className="min-h-screen bg-background">
      <main className="flex-1 pb-20">
        <Suspense fallback={<PageLoader />}>
          {children}
        </Suspense>
      </main>
      <BottomNav />
    </div>
  );
};

// Redireciona instaladores de /jobs/:id para /installer/job/:id
// Admin/Manager continuam vendo o JobDetail normal
const JobDetailRouter = () => {
  const { jobId } = useParams();
  const { user } = useAuth();
  if (user?.role === 'installer') {
    return <Navigate to={`/installer/job/${jobId}`} replace />;
  }
  return (
    <MainLayout>
      <Suspense fallback={<PageLoader />}>
        <JobDetail />
      </Suspense>
    </MainLayout>
  );
};

const AppRoutes = () => {
  const { user } = useAuth();

  return (
    <Routes>
      <Route
        path="/login"
        element={
          user ? <Navigate to="/dashboard" replace /> : (
            <Suspense fallback={<PageLoader />}>
              <Login />
            </Suspense>
          )
        }
      />
      <Route
        path="/register"
        element={
          user ? <Navigate to="/dashboard" replace /> : (
            <Suspense fallback={<PageLoader />}>
              <Register />
            </Suspense>
          )
        }
      />
      <Route
        path="/forgot-password"
        element={
          user ? <Navigate to="/dashboard" replace /> : (
            <Suspense fallback={<PageLoader />}>
              <ForgotPassword />
            </Suspense>
          )
        }
      />
      <Route
        path="/reset-password"
        element={
          user ? <Navigate to="/dashboard" replace /> : (
            <Suspense fallback={<PageLoader />}>
              <ResetPassword />
            </Suspense>
          )
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Dashboard />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/jobs"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Jobs />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/jobs/:jobId"
        element={
          <ProtectedRoute>
            <JobDetailRouter />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager']}>
            <MainLayout>
              <Users />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Calendar />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/installer/dashboard"
        element={
          <ProtectedRoute>
            <InstallerLayout>
              <InstallerDashboard />
            </InstallerLayout>
          </ProtectedRoute>
        }
      />
      {/* Removed obsolete routes: /checkin/:jobId and /checkout/:checkinId
          These were replaced by the item-based check-in/out flow in InstallerJobDetail */}
      <Route
        path="/checkin-viewer/:checkinId"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CheckinViewer />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/checkins"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Checkins />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <MainLayout>
              <UnifiedReports />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/family"
        element={
          <ProtectedRoute>
            <MainLayout>
              <FamilyReport />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/installer"
        element={
          <ProtectedRoute>
            <MainLayout>
              <InstallerReport />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/kpis"
        element={
          <ProtectedRoute>
            <MainLayout>
              <FamilyKPIsReport />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      {/* Redirect old productivity route to unified reports */}
      <Route
        path="/reports/productivity"
        element={<Navigate to="/reports" replace />}
      />
      {/* Redirect old metrics route to unified reports */}
      <Route
        path="/metrics"
        element={<Navigate to="/reports" replace />}
      />
      <Route
        path="/installer/calendar"
        element={
          <ProtectedRoute>
            <InstallerLayout>
              <InstallerCalendar />
            </InstallerLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/installer/job/:jobId"
        element={
          <ProtectedRoute>
            <InstallerJobDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Profile />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      {/* [GAMIFICATION REMOVIDA 2026-06-11] redirects mantidos para bookmarks antigos. */}
      <Route path="/loja-faixa-preta" element={<Navigate to="/dashboard" replace />} />
      <Route path="/gamification-report" element={<Navigate to="/dashboard" replace />} />
      <Route
        path="/admin/scheduler"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <MainLayout>
              <SchedulerAdmin />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/visitas-tecnicas"
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager', 'installer']}>
            <MainLayout>
              <VisitasTecnicas />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/visitas-tecnicas/relatorios"
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager']}>
            <MainLayout>
              <VisitasRelatorios />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/visitas-tecnicas/:id"
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager', 'installer']}>
            <MainLayout>
              <VisitaDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/"
        element={<Navigate to={user ? "/dashboard" : "/login"} replace />}
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="dark" data-theme="dark">
          <OfflineBanner />
          <ErrorBoundary>
            <AppRoutes />
          </ErrorBoundary>
          <UpdateNotification />
          <Toaster 
            position="top-right"
            toastOptions={{
              style: {
                background: 'hsl(var(--card))',
                color: 'hsl(var(--foreground))',
                border: '1px solid hsl(var(--border))',
              },
            }}
          />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;